import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { Grid, Typography } from "@material-ui/core";
import { CreateRewardMutation, CreateRewardMutationVariables, RewardReason } from "generated/graphql";
import { successToast } from "Modules/DPToast";
import { handleValidationErrors } from "Modules/HandleValidationErrors";
import { DPButton, DPSpacer } from "Components/DP";
import { FieldsGroup } from "Components/Shared/FormComponents/FieldsGroup/FieldsGroup";
import { MessageToEmployeeField } from "./MessageToEmployeeField/MessageToEmployeeField";
import { ReasonSelect } from "./ReasonSelect/ReasonSelect";
import { RewardAmountField } from "./RewardAmountField/RewardAmountField";
import mutations from "Shared/mutations";
import moneyToCents from "Shared/money";
import queries from "Shared/queries";
import { useRemainingRewardBudget } from "../useRemainingRewardBudget";
import useStyles from "./styles";
import { Option } from "react-select/src/filters";

export interface CreateRewardFormFields {
  amount: string;
  notes?: string;
  reason?: Option | null;
}

export interface CreateRewardFormProps {
  employeeProfileId: string;
  refetchRewards: () => void;
}

export const CreateRewardForm = ({
  employeeProfileId,
  refetchRewards,
}: CreateRewardFormProps) => {
  const classes = useStyles();
  const [createRewardFn] = useMutation<CreateRewardMutation, CreateRewardMutationVariables>(
    mutations.CREATE_REWARD
  );
  const { remainingRewardBudget, loading } = useRemainingRewardBudget();

  const defaultFormValues = {
    amount: "",
    notes: "",
    reason: null,
  };

  const formMethods = useForm<CreateRewardFormFields>({
    defaultValues: defaultFormValues,
    mode: "onBlur",
  });

  const { getValues, trigger, reset } = formMethods;

  const onSubmit = async () => {
    const isValid = await trigger();
    const formValues = getValues();
    if (isValid) {
      try {
        const response = await createRewardFn({
          variables: {
            createRewardInput: {
              employeeProfileId,
              totalAmountCents: moneyToCents(formValues.amount),
              notes: formValues.notes,
              reason: formValues.reason?.value as RewardReason
            }
          },
          refetchQueries: [
            { query: queries.GET_REMAINING_REWARD_BUDGET },
            { query: queries.PARTNER_CONFIG_QUERY },
          ],
        });

        refetchRewards();
        successToast(response.data?.createReward.message || "");
        reset(defaultFormValues);
      } catch (err) {
        handleValidationErrors(err);
      }
    }
  };

  if (loading) {
    return null;
  }

  return remainingRewardBudget
    ? (
      <FormProvider {...formMethods}>
        <FieldsGroup>
          <RewardAmountField remainingRewardBudget={remainingRewardBudget} />
          <MessageToEmployeeField />
          <ReasonSelect />
        </FieldsGroup>
        <DPSpacer spacing={4} />
        <Grid container justify="center" >
          <DPButton onClick={onSubmit}>
            Send Reward
          </DPButton>
        </Grid>
      </FormProvider>
    ) : (
      <Typography className={classes.disabledRewards}>
        You cannot send reward at this time,
        as you have reached your reward budget limit for this month.
      </Typography>
    );
};
